import { CheckBox } from "#components";
import type {
  FieldInput,
  BooleanFieldInput,
  CheckboxFieldInput,
  NumberFieldInput,
  RadioFieldInput,
  RangeFieldInput,
  SelectFieldInput,
  SliderFieldInput,
  TextareaFieldInput,
  TextFieldInput,
  SelectFieldInputItem,
} from "../types/fields";
import type {
  FieldInputsWithoutKey,
  FieldInput as FormFieldInput,
} from "~/layers/ui/modules/VDynamicForm/runtime/types";
import defu from "defu";

const isAllowedFieldInputRule = (rule: string) => {
  const x = ["boolean", "nullable"];
  return !x.includes(rule);
};

export const isBooleanIshFieldInput = (
  input?: FieldInput
): input is BooleanFieldInput => {
  return isBooleanFieldInput(input) || isRadioFieldInput(input);
};

export const isBooleanFieldInput = (
  input?: FieldInput
): input is BooleanFieldInput => {
  return !!input && (input as BooleanFieldInput).input == "boolean";
};

export const isCheckboxFieldInput = (
  input?: FieldInput
): input is CheckboxFieldInput => {
  return !!input && (input as CheckboxFieldInput).input == "checkbox";
};

export const isNumberFieldInput = (
  input?: FieldInput
): input is NumberFieldInput => {
  return (
    !!input &&
    (input as NumberFieldInput).input == "text" &&
    (input as NumberFieldInput)?.type == "number"
  );
};

export const isRadioFieldInput = (
  input?: FieldInput
): input is RadioFieldInput => {
  return !!input && (input as RadioFieldInput).input == "radio";
};

export const isRangeFieldInput = (
  input?: FieldInput
): input is RangeFieldInput => {
  return !!input && (input as RangeFieldInput).input == "range";
};

export const isSelectFieldInput = (
  input?: FieldInput
): input is SelectFieldInput => {
  return !!input && (input as SelectFieldInput).input == "select";
};

export const isSliderFieldInput = (
  input?: FieldInput
): input is SliderFieldInput => {
  return !!input && (input as SliderFieldInput).input == "slider";
};

export const isTextFieldInput = (
  input?: FieldInput
): input is TextFieldInput => {
  return !!input && (input as TextFieldInput).input == "text";
};

export const isTextareaFieldInput = (
  input?: FieldInput
): input is TextareaFieldInput => {
  return !!input && (input as TextareaFieldInput).input == "textarea";
};

export const fieldToFormInput = (
  input: FieldInput,
  def?: RecursivePartial<FormFieldInput>
): FormFieldInput => {
  const base = defu(def, {
    label: input.label,
    name: input.label,
    key: input.name,
    rules: wrap(input.rules).filter(isAllowedFieldInputRule),
    readonly: input.readonly,
    // tipInfo
    props: {
      placeholder: input.placeholder,
      hint: input?.hint,
      persistentHint: input.persistentHint,
    },
  });
  if (isBooleanFieldInput(input)) {
    return {
      ...base,
      component: CheckBox,
      // cast: "boolean",
      class: "mb-1",
      props: {
        ...base.props,
        title: input.label,
        subtitle: input.hint,
        trueValue: input.trueValue,
        falseValue: input.falseValue,
      },
    };
  }
  if (isCheckboxFieldInput(input)) {
    return {
      ...base,
      component: CheckBox,
      cast: "boolean",
      class: "mb-1",
      props: {
        ...base.props,
        title: input.label,
      },
    };
  }
  if (isNumberFieldInput(input)) {
    return {
      ...base,
      type: "text",
      cast: "number",
      props: {
        ...base.props,
        type: "number",
        // integer: input.integer,
        min: input.min,
        max: input.max,
        step: input.step,
      },
    };
  }
  if (isRadioFieldInput(input)) {
    return {
      ...base,
      type: "radio",
      props: {
        ...base.props,
        trueValue: input.trueValue,
        falseValue: input.falseValue,
      },
    };
  }
  if (isRangeFieldInput(input)) {
    return {
      ...base,
      type: "range-slider",
    };
  }
  if (isSelectFieldInput(input)) {
    const items = (input.items || []) as SelectFieldInputItem[];

    return {
      ...base,
      type: "select",
      props: {
        ...base.props,
        multiple: input.multiple,
        items: items.map((item) => ({
          title: item.text,
          value: item.value,
        })),
      },
    };
  }
  if (isSliderFieldInput(input)) {
    return {
      ...base,
      type: "slider",
    };
  }
  if (isTextFieldInput(input)) {
    return {
      ...base,
      type: "text",
    };
  }
  if (isTextareaFieldInput(input)) {
    return {
      ...base,
      type: "textarea",
      props: {
        ...base.props,
        rows: input.rows,
      },
    };
  }

  return {
    ...base,
    type: "text",
  };
};

export const fieldsToFormInputs = (
  inputs: FieldInput[],
  defu?: RecursivePartial<FormFieldInput>
) => {
  const x: FieldInputsWithoutKey = {};
  return inputs.reduce((acc, input) => {
    const x = fieldToFormInput(input, defu);
    acc[x.key] = x;
    return acc;
  }, x);
};
